import { createContext, useContext, useEffect, useMemo, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { Me, OperatorPublic } from "@ftpedi/contracts";
import { api, ApiError, onAuthError } from "./api.ts";

export type Surface = OperatorPublic["surface"];

export interface SessionState {
  me: Me | null;
  operator: OperatorPublic;
  surface: Surface;
  features: Set<string>;
  isOperator: boolean;
  isCustomer: boolean;
  isPlatform: boolean;
  isOperatorAdmin: boolean;
  isCustomerAdmin: boolean;
  hasFeature: (f: string) => boolean;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
}

const Ctx = createContext<SessionState | null>(null);

export const operatorQueryKey = ["operator"] as const;
export const meQueryKey = ["me"] as const;

export const fetchOperator = () => api.get<OperatorPublic>("/api/operator");
export const fetchMe = async (): Promise<Me | null> => {
  try {
    return await api.get<Me>("/api/me");
  } catch (e) {
    if (e instanceof ApiError && e.status === 401) return null;
    throw e;
  }
};

export function SessionProvider({ operator, children }: { operator: OperatorPublic; children: ReactNode }) {
  const qc = useQueryClient();
  const meQ = useQuery({ queryKey: meQueryKey, queryFn: fetchMe, staleTime: 60_000, retry: false });

  useEffect(
    () =>
      onAuthError((status) => {
        if (status === 401) qc.setQueryData(meQueryKey, null);
      }),
    [qc],
  );

  const value = useMemo<SessionState>(() => {
    const me = meQ.data ?? null;
    const role = me?.role;
    const features = new Set(operator.features);
    return {
      me,
      operator,
      surface: operator.surface,
      features,
      isOperator: role === "operator_admin" || role === "operator_staff" || role === "platform_admin",
      isCustomer: role === "customer_admin" || role === "customer_user",
      isPlatform: role === "platform_admin",
      isOperatorAdmin: role === "operator_admin" || role === "platform_admin",
      isCustomerAdmin: role === "customer_admin",
      hasFeature: (f) => features.has(f),
      refresh: async () => {
        await qc.invalidateQueries({ queryKey: meQueryKey });
      },
      signOut: async () => {
        try {
          await api.post("/api/auth/logout");
        } finally {
          qc.setQueryData(meQueryKey, null);
          qc.clear();
          window.location.assign("/login");
        }
      },
    };
  }, [meQ.data, operator, qc]);

  if (meQ.isPending) return null;
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSession(): SessionState {
  const v = useContext(Ctx);
  if (!v) throw new Error("useSession outside SessionProvider");
  return v;
}

export function useMe(): Me {
  const { me } = useSession();
  if (!me) throw new Error("Not signed in");
  return me;
}
